import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductFormat, Publisher, SlotType, wooId } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'manage-gambling-estimates',
  templateUrl: './manage-gambling-estimates.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageGamblingEstimates implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;

  show = {
    estimatesHaveChanged: false,
    updatingEstimates: false,
    loading: true,
    isEditing: false,
  };

  readonly tableHeaders = [
    'Publicist',
    'Placering',
    'Pause Ad',
    'Preroll',
    'Midroll',
    'Postroll',
    'Pause Ad',
    'Preroll',
    'Midroll',
    'Postroll',
  ];

  readonly staticFields = 2;

  publishers = [] as Publisher[];
  selectedPublisher: Publisher;
  productFormats: ProductFormat[];
  selectedProductFormat: ProductFormat;
  gamblingRows: GamblingRow[];
  includedProductFormatSlotTypes: Record<wooId, Record<SlotType, boolean>> = {};
  translateProductFormat: (args: any) => string;

  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private modalService: ModalService,
    private utilityService: ProductsPageUtilityService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.translateProductFormat = this.translationService.convertProductFormat;
    this.publishers = (await this.publisherService.getPublishers(ExtraPublisherField.enabled)).filter((p) => p.enabled);
    try {
      await this.getAndBuildGamblingRows();
    } finally {
      this.show.loading = false;
      this.changeDetection.markForCheck();
    }
  }

  async getAndBuildGamblingRows(): Promise<void> {
    const productFormats = await this.productFormatService.getProductFormats(
      ExtraProductFormatField.enabled,
      ExtraProductFormatField.gamblingPauseAdShare,
      ExtraProductFormatField.gamblingPauseAdWithdrawalLimit,
      ExtraProductFormatField.gamblingWithdrawalLimitShares,
      ExtraProductFormatField.gamblingWithdrawalLimits,
      ExtraProductFormatField.slotTypes,
    );
    this.productFormats = productFormats.filter((f) => f.enabled);
    this.includedProductFormatSlotTypes = this.utilityService.setIncludedProductFormatSlotTypes(this.productFormats);
    this.buildGamblingRows();
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  async cancelEditing(): Promise<void> {
    this.setEditing(false);
    await this.getAndBuildGamblingRows();
  }

  async save(): Promise<void> {
    const filterActive = this.selectedPublisher || this.selectedProductFormat;
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then(async (status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          try {
            this.show.updatingEstimates = true;
            this.productFormats = await this.productFormatService.updateProductFormats(this.productFormats);
            await this.buildGamblingRows();
            this.show.estimatesHaveChanged = true;
          } catch {
          } finally {
            loaderModal.close();
            this.setEditing(false);
            this.show.updatingEstimates = false;
            this.modalService.closeAll();
            this.changeDetection.markForCheck();
          }
        }
      });
  }

  rowVisible(obj: GamblingRow): boolean {
    const publisherFiltered = this.selectedPublisher && obj.publisherId !== this.selectedPublisher.id;
    const productFormatFiltered = this.selectedProductFormat && obj.productFormatId !== this.selectedProductFormat.id;
    return !(publisherFiltered || productFormatFiltered);
  }

  getGamblingProductFormat(productFormatId: wooId): ProductFormat {
    return this.productFormats.find((format) => format.id === productFormatId);
  }

  private buildGamblingRows(): GamblingRow[] {
    let gamblingRows: GamblingRow[] = [];

    this.publishers.map((publisher) => {
      this.utilityService.getPublisherSpecificProductFormats(publisher, this.productFormats).map((product_format) => {
        gamblingRows = [
          ...gamblingRows,
          {
            publisher: this.utilityService.getPublisherName(publisher.id, this.publishers),
            publisherId: publisher.id,
            productFormat: product_format.name,
            productFormatId: product_format.id,
            pauseAdShare: product_format.gambling_pause_ad_share,
            sharesPreroll: product_format.gambling_withdrawal_limit_shares.preroll,
            sharesMidroll: product_format.gambling_withdrawal_limit_shares.midroll,
            sharesPostroll: product_format.gambling_withdrawal_limit_shares.postroll,
            pausAdWithdrawalLimit: product_format.gambling_pause_ad_withdrawal_limit,
            withdrawalLimitsPreroll: product_format.gambling_withdrawal_limits.preroll,
            withdrawalLimitsMidroll: product_format.gambling_withdrawal_limits.midroll,
            withdrawalLimitsPostroll: product_format.gambling_withdrawal_limits.postroll,
          },
        ];
      });
    });
    this.gamblingRows = gamblingRows;
    return gamblingRows;
  }
}

interface GamblingRow {
  publisher: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId: wooId;
  pauseAdShare?: number;
  sharesPreroll?: number;
  sharesMidroll?: number;
  sharesPostroll?: number;
  pausAdWithdrawalLimit?: number;
  withdrawalLimitsPreroll?: number;
  withdrawalLimitsMidroll?: number;
  withdrawalLimitsPostroll?: number;
}
